import { motion } from 'framer-motion';
import { Clock, MapPin, Users, Calendar } from 'lucide-react';
import React from 'react';

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const EventCard = ({ event, employees, onClick, compact = false }) => {
  const getEmployeeNames = (employeeIds) => {
    return employeeIds.map(id => {
      const employee = employees.find(e => e.id === id);
      return employee ? employee.name : 'Unknown';
    });
  };

  const getEmployeeAvatars = (employeeIds, max = 3) => {
    const selectedEmployees = employees.filter(e => employeeIds.includes(e.id));
    return selectedEmployees.slice(0, max);
  };

  const getEventColor = (event) => {
    const colors = {
      primary: {
        bg: 'bg-primary-50',
        border: 'border-primary-200',
        text: 'text-primary-700',
        icon: 'text-primary-600'
      },
      secondary: {
        bg: 'bg-secondary-50',
        border: 'border-secondary-200',
        text: 'text-secondary-700',
        icon: 'text-secondary-600'
      },
      accent: {
        bg: 'bg-accent-50',
        border: 'border-accent-200',
        text: 'text-accent-700',
        icon: 'text-accent-600'
      },
      warning: {
        bg: 'bg-warning-50',
        border: 'border-warning-200',
        text: 'text-warning-700',
        icon: 'text-warning-600'
      },
      success: {
        bg: 'bg-success-50',
        border: 'border-success-200',
        text: 'text-success-700',
        icon: 'text-success-600'
      }
    };
    return colors[event.color] || colors.primary;
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      meeting: Users,
      task: Calendar,
      reminder: Clock,
      event: Calendar
    };
    return icons[type] || Calendar;
  };

  const formatEventType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const colors = getEventColor(event);
  const EventTypeIcon = getEventTypeIcon(event.type);
  const taggedEmployees = getEmployeeAvatars(event.taggedEmployees);

  if (compact) {
    return (
      <motion.div
        className={`p-2 rounded-lg border ${colors.bg} ${colors.border} cursor-pointer hover:shadow-md transition-all`}
        onClick={onClick}
        variants={itemVariants}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center space-x-2">
          <div className={`p-1 rounded ${colors.bg} ${colors.icon}`}>
            <EventTypeIcon size={12} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`text-xs font-medium ${colors.text} truncate`}>
              {event.title}
            </h4>
            <div className="flex items-center text-xs text-gray-500 mt-0.5">
              <Clock size={10} className="mr-1" />
              <span>{event.time}</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`p-4 rounded-lg border ${colors.bg} ${colors.border} cursor-pointer hover:shadow-lg transition-all group`}
      onClick={onClick}
      variants={itemVariants}
      whileHover={{ scale: 1.02, y: -3 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${colors.bg} ${colors.icon}`}>
            <EventTypeIcon size={16} />
          </div>
          <div>
            <h4 className={`font-medium ${colors.text} group-hover:${colors.icon} transition-colors`}>
              {event.title}
            </h4>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {formatEventType(event.type)}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {event.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {event.description}
        </p>
      )}

      {/* Time and Location */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm text-gray-600">
          <Clock size={14} className="mr-2 text-gray-400" />
          <span>
            {event.time} - {event.endTime}
          </span>
        </div>

        {event.location && (
          <div className="flex items-center text-sm text-gray-600">
            <MapPin size={14} className="mr-2 text-gray-400" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>

      {/* Tagged Employees */}
      {event.taggedEmployees.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Users size={14} className="mr-2 text-gray-400" />
            <span className="text-sm text-gray-600 mr-2">Tagged:</span>
            <div className="flex -space-x-1">
              {taggedEmployees.map((employee, index) => (
                <div
                  key={employee.id}
                  className="w-6 h-6 rounded-full bg-primary-100 border-2 border-white flex items-center justify-center text-xs font-medium text-primary-700"
                  title={employee.name}
                >
                  {employee.avatar}
                </div>
              ))}
              {event.taggedEmployees.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                  +{event.taggedEmployees.length - 3}
                </div>
              )}
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {getEmployeeNames(event.taggedEmployees).join(', ')}
          </div>
        </div>
      )}

      {/* Hover Effect Indicator */}
      <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-primary-300 transition-colors pointer-events-none" />
    </motion.div>
  );
};

export default EventCard;
